@if (isVisible) {
    @if (tourStep) {
        <!-- Guided first session: the page darkens, the checklist or the field of this step stays bright. -->
        @if (spotlight) {
            <div
                class="sidekick-tour__spot"
                aria-hidden="true"
                [style.top.px]="spotlight.top"
                [style.left.px]="spotlight.left"
                [style.width.px]="spotlight.width"
                [style.height.px]="spotlight.height"></div>
        } @else {
            <div class="sidekick-tour__veil" aria-hidden="true"></div>
        }
    }
    @if (confettiOrigin) {
        <!-- The onboarding is done: the sidekick's dots as confetti, out over the page and back. -->
        <div class="sidekick-confetti" aria-hidden="true">
            @for (piece of confetti; track $index) {
                <span
                    class="sidekick-confetti__piece"
                    [class.is-ink]="piece.isInk"
                    [class.is-square]="piece.isSquare"
                    [style.left.px]="confettiOrigin.x"
                    [style.top.px]="confettiOrigin.y"
                    [style.width.px]="piece.size"
                    [style.height.px]="piece.size"
                    [style.--dx]="piece.dx"
                    [style.--dy]="piece.dy"
                    [style.--spin]="piece.spin"
                    [style.animation-delay]="piece.delay"></span>
            }
        </div>
    }
    @if (isBeingBorn) {
        <!-- After registering: the sidekick puts itself together in the middle of the start page. -->
        <div class="sidekick-birth" [class.is-leaving]="birthPhase === 'landing'">
            <div class="sidekick-birth__caption-area" aria-live="polite">
                @if (birthPhase === 'named' && coachName) {
                    <p class="sidekick-birth__caption is-shown">
                        {{ 'sidekick.naming.hello' | translate: { name: coachName } }}
                    </p>
                } @else {
                    <p class="sidekick-birth__caption" [class.is-shown]="birthPhase === 'alive' || birthPhase === 'naming'">
                        {{ 'sidekick.birth' | translate }}
                    </p>
                }
                @if (birthPhase === 'naming') {
                    <div class="sidekick-birth__naming">
                        <p class="sidekick-birth__question">{{ 'sidekick.naming.title' | translate }}</p>
                        <form class="sidekick-name" (submit)="$event.preventDefault(); saveName()" novalidate>
                            <label class="visually-hidden" for="sidekick-birth-name">{{
                                'sidekick.naming.label' | translate
                            }}</label>
                            <input
                                id="sidekick-birth-name"
                                class="sidekick-name__input"
                                name="sidekickName"
                                type="text"
                                autocomplete="off"
                                autocapitalize="words"
                                enterkeyhint="done"
                                [attr.maxlength]="nameMaxLength"
                                [placeholder]="'sidekick.naming.placeholder' | translate"
                                [value]="nameDraft"
                                (input)="onNameInput($any($event.target).value)"
                                [attr.aria-invalid]="nameError ? 'true' : null"
                                [attr.aria-describedby]="nameError ? 'sidekick-birth-name-error' : null" />
                            @if (nameError) {
                                <p class="sidekick-name__error" [id]="'sidekick-birth-name-error'" role="alert">
                                    {{ 'sidekick.naming.' + nameError | translate }}
                                </p>
                            }
                            <div class="sidekick-name__actions">
                                <button type="submit" class="sidekick__cta sidekick-name__save" [disabled]="isSavingName">
                                    {{ 'sidekick.naming.save' | translate }}
                                </button>
                                <button type="button" class="sidekick-name__later" (click)="skipNaming()">
                                    {{ 'sidekick.naming.later' | translate }}
                                </button>
                            </div>
                        </form>
                    </div>
                }
            </div>
        </div>
    }
    <aside
        [class]="'sidekick birth-' + birthPhase + ' bubble-' + bubblePlacement"
        [style.--sidekick-lift.px]="bubbleLift"
        [attr.aria-label]="
            coachName ? coachName + ' · ' + ('sidekick.naming.coCoach' | translate) : ('sidekick.name' | translate)
        ">
        <div class="sidekick__live" aria-live="polite">
            @if (isBubbleShown && message) {
                <div
                    class="sidekick__bubble"
                    (mouseenter)="pauseBubble(true)"
                    (mouseleave)="pauseBubble(false)"
                    (focusin)="pauseBubble(true)"
                    (focusout)="pauseBubble(false)">
                    @if (coachName) {
                        <p class="sidekick__who">{{ coachName }} · {{ 'sidekick.naming.coCoach' | translate }}</p>
                    }
                    <p class="sidekick__text">
                        <!-- Typed like on a typewriter; screen readers get it in full, the hidden copy sets the size. -->
                        <span class="visually-hidden">{{ fullText }}</span>
                        <span class="sidekick__text-size" aria-hidden="true">{{ fullText }}</span>
                        <span class="sidekick__text-typed" [class.is-typing]="isTyping" aria-hidden="true">{{
                            typedText
                        }}</span>
                    </p>
                    @if (message.cta?.action) {
                        <button type="button" class="sidekick__cta" (click)="onCta()">
                            {{ ctaLabel }}
                        </button>
                    } @else if (message.cta) {
                        <a
                            class="sidekick__cta"
                            [routerLink]="message.cta.route"
                            [queryParams]="message.cta.queryParams"
                            (click)="onCta()">
                            {{ message.cta.label | translate: messageParams }}
                        </a>
                    }
                    <button
                        type="button"
                        class="sidekick__close"
                        (click)="closeMessage()"
                        [attr.aria-label]="'sidekick.close' | translate"
                        [title]="'sidekick.close' | translate">
                        <i-feather name="x"></i-feather>
                    </button>
                </div>
            }
        </div>

        @if (isNameOpen) {
            <section class="sidekick__steps sidekick__name-panel" [attr.aria-label]="'sidekick.naming.title' | translate">
                <header class="sidekick__steps-header">
                    <h2 class="sidekick__steps-title">{{ 'sidekick.naming.title' | translate }}</h2>
                    <button
                        type="button"
                        class="sidekick__close"
                        (click)="closeNamePanel()"
                        [attr.aria-label]="'sidekick.close' | translate"
                        [title]="'sidekick.close' | translate">
                        <i-feather name="x"></i-feather>
                    </button>
                </header>
                <p class="sidekick-name__hint">{{ 'sidekick.naming.hint' | translate }}</p>
                <form class="sidekick-name" (submit)="$event.preventDefault(); saveName()" novalidate>
                    <label class="visually-hidden" for="sidekick-name">{{ 'sidekick.naming.label' | translate }}</label>
                    <input
                        id="sidekick-name"
                        class="sidekick-name__input"
                        name="sidekickName"
                        type="text"
                        autocomplete="off"
                        autocapitalize="words"
                        enterkeyhint="done"
                        [attr.maxlength]="nameMaxLength"
                        [placeholder]="'sidekick.naming.placeholder' | translate"
                        [value]="nameDraft"
                        (input)="onNameInput($any($event.target).value)"
                        [attr.aria-invalid]="nameError ? 'true' : null"
                        [attr.aria-describedby]="nameError ? 'sidekick-name-error' : null" />
                    @if (nameError) {
                        <p class="sidekick-name__error" [id]="'sidekick-name-error'" role="alert">
                            {{ 'sidekick.naming.' + nameError | translate }}
                        </p>
                    }
                    <div class="sidekick-name__actions">
                        <button type="submit" class="sidekick__cta sidekick-name__save" [disabled]="isSavingName">
                            {{ 'sidekick.naming.save' | translate }}
                        </button>
                        <button type="button" class="sidekick-name__later" (click)="closeNamePanel()">
                            {{ (coachName ? 'sidekick.naming.cancel' : 'sidekick.naming.later') | translate }}
                        </button>
                    </div>
                </form>
            </section>
        }

        @if (isChecklistOpen) {
            <section class="sidekick__steps" [attr.aria-label]="'onboarding.title' | translate">
                <header class="sidekick__steps-header">
                    <h2 class="sidekick__steps-title">{{ 'onboarding.title' | translate }}</h2>
                    <span class="sidekick__steps-count">{{ doneCount }}/{{ steps.length }}</span>
                    <button
                        type="button"
                        class="sidekick__close"
                        (click)="tourStep === 'checklist' ? closeMessage() : closeMenu()"
                        [attr.aria-label]="'onboarding.collapse' | translate"
                        [title]="'onboarding.collapse' | translate">
                        <i-feather name="x"></i-feather>
                    </button>
                </header>
                @if (tourStep === 'checklist' && message) {
                    <!-- The co-coach's words for this step sit in the checklist: one panel, not two. -->
                    <div class="sidekick__steps-note" aria-live="polite">
                        @if (coachName) {
                            <p class="sidekick__who">{{ coachName }} · {{ 'sidekick.naming.coCoach' | translate }}</p>
                        }
                        <p class="sidekick__text">
                            <!-- Typed like on a typewriter; screen readers get it in full, the hidden copy sets the size. -->
                            <span class="visually-hidden">{{ fullText }}</span>
                            <span class="sidekick__text-size" aria-hidden="true">{{ fullText }}</span>
                            <span class="sidekick__text-typed" [class.is-typing]="isTyping" aria-hidden="true">{{
                                typedText
                            }}</span>
                        </p>
                        @if (message.cta?.action) {
                            <button type="button" class="sidekick__cta" (click)="onCta()">
                                {{ message.cta!.label | translate: messageParams }}
                            </button>
                        }
                    </div>
                }
                <div
                    class="sidekick__bar"
                    role="progressbar"
                    aria-valuemin="0"
                    aria-valuemax="100"
                    [attr.aria-valuenow]="progress">
                    <div class="sidekick__bar-fill" [style.width.%]="progress"></div>
                </div>
                <ol class="sidekick__step-list">
                    @for (step of steps; track step.key) {
                        <li>
                            <a
                                class="sidekick__step"
                                [class.done]="isDone(step)"
                                [class.is-next]="step.key === nextOpenStep?.key"
                                [routerLink]="step.route"
                                [queryParams]="step.queryParams"
                                (click)="closeMenu()">
                                <span class="sidekick__check" aria-hidden="true">
                                    @if (isDone(step)) {
                                        <i-feather name="check"></i-feather>
                                    }
                                </span>
                                <span class="sidekick__step-text">
                                    <span class="sidekick__step-title"
                                        >{{ step.label + '.title' | translate }}
                                        @if (stepProgress(step); as count) {
                                            <span class="sidekick__step-count">{{ count }}</span>
                                        }
                                    </span>
                                    <span class="sidekick__step-hint">{{ step.label + '.hint' | translate }}</span>
                                </span>
                                @if (step.key === nextOpenStep?.key) {
                                    <i-feather class="sidekick__step-go" name="arrow-right" aria-hidden="true"></i-feather>
                                }
                                <span class="visually-hidden">
                                    {{ (isDone(step) ? 'onboarding.done' : 'onboarding.open') | translate }}
                                </span>
                            </a>
                        </li>
                    }
                </ol>
            </section>
        }

        @if (isMenuOpen) {
            <div class="sidekick__menu" role="menu" [attr.aria-label]="'sidekick.options' | translate">
                <button type="button" role="menuitem" class="sidekick__menu-item" (click)="openNamePanel()">
                    <i-feather name="edit-2"></i-feather>
                    {{ (coachName ? 'sidekick.naming.rename' : 'sidekick.naming.give') | translate }}
                </button>
                <button type="button" role="menuitem" class="sidekick__menu-item" (click)="toggleMuted()">
                    <i-feather [name]="isMuted ? 'volume-2' : 'volume-x'"></i-feather>
                    {{ (isMuted ? 'sidekick.unmute' : 'sidekick.mute') | translate }}
                </button>
                <button type="button" role="menuitem" class="sidekick__menu-item" (click)="hide()">
                    <i-feather name="eye-off"></i-feather>
                    {{ 'sidekick.hide' | translate }}
                </button>
            </div>
        }

        <button
            type="button"
            class="sidekick__disc"
            [class.is-muted]="isMuted"
            (click)="toggleMenu()"
            (mouseenter)="setHovered(true)"
            (mouseleave)="setHovered(false)"
            (focus)="setHovered(true)"
            (blur)="setHovered(false)"
            aria-haspopup="menu"
            [attr.aria-expanded]="isMenuOpen"
            [attr.aria-label]="'sidekick.options' | translate"
            [title]="'sidekick.options' | translate">
            <svg
                [class]="'sidekick__svg mood-' + mood"
                [class.is-scattered]="isScattered"
                [attr.viewBox]="'0 0 ' + disc + ' ' + disc"
                aria-hidden="true"
                focusable="false">
                <circle class="sidekick__ground" [attr.cx]="disc / 2" [attr.cy]="disc / 2" [attr.r]="disc / 2"></circle>
                <g class="sidekick__mark">
                    <g [attr.transform]="markTransform">
                        <g class="sidekick__motion">
                            @for (dot of motionDots; track $index) {
                                <circle
                                    class="sidekick__dot"
                                    [class.flicker]="dot.flicker"
                                    [attr.cx]="dot.cx"
                                    [attr.cy]="dot.cy"
                                    [attr.r]="radius"
                                    [style.transform]="isScattered ? (isBeingBorn ? dot.birthIntro : dot.intro) : null"
                                    [style.transition-delay]="isBeingBorn ? dot.birthDelay : null"></circle>
                            }
                            @for (dot of trail; track $index) {
                                <circle
                                    class="sidekick__trail"
                                    [class.far]="dot.step === 2"
                                    [attr.cx]="dot.cx"
                                    [attr.cy]="dot.cy"
                                    [attr.r]="radius"></circle>
                            }
                        </g>
                        <g class="sidekick__player">
                            @for (dot of playerDots; track $index) {
                                <circle
                                    class="sidekick__dot"
                                    [attr.cx]="dot.cx"
                                    [attr.cy]="dot.cy"
                                    [attr.r]="radius"
                                    [style.transform]="isScattered ? (isBeingBorn ? dot.birthIntro : dot.intro) : null"
                                    [style.transition-delay]="isBeingBorn ? dot.birthDelay : null"></circle>
                            }
                        </g>
                        <g class="sidekick__sparks">
                            @for (spark of sparks; track $index) {
                                <circle
                                    class="sidekick__spark"
                                    [attr.cx]="spark.cx"
                                    [attr.cy]="spark.cy"
                                    [attr.r]="radius"></circle>
                            }
                        </g>
                    </g>
                </g>
            </svg>
            @if (message && bubblePlacement === 'parked') {
                <!-- A tip waits here because it would cover a button; the disc opens it. -->
                <span class="sidekick__waiting" aria-hidden="true">
                    <i-feather name="message-circle"></i-feather>
                </span>
            }
            @if (hasOpenSteps && !isBeingBorn) {
                <!-- Progress of the getting-started steps; the disc opens them. -->
                <span class="sidekick__badge" aria-hidden="true">{{ doneCount }}/{{ steps.length }}</span>
            }
        </button>
    </aside>
    <!-- Room at the end of the page, so the sidekick never covers the last buttons (e.g. RUN). -->
    <div class="sidekick-spacer" aria-hidden="true"></div>
}
